import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import type {
  FilterTree,
  ViewEntityType,
  ViewColumnSpec,
  ViewScope,
  ViewSort,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth-context";
import { useCreateView } from "@/features/admin/views/use-create-view";

// "Save view as…" dialog. The active filter/columns/sort from the parent
// page are passed in via `initialState`; the user names the view and
// chooses a scope. Shared scope requires `settings.write` — gated upstream
// in the service layer too; the UI hides the option when the session
// can't pick it.
//
// Replaces the legacy components/shared/views/SaveViewDialog.tsx, scoped
// to the SPA mutation hook + auth context.

export type SaveViewInitialState = {
  filter: FilterTree | null;
  columns: ViewColumnSpec[];
  sort: ViewSort | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: ViewEntityType;
  initialState: SaveViewInitialState;
};

export function SaveViewDialog({
  open,
  onOpenChange,
  entityType,
  initialState,
}: Props) {
  const session = useAuth().session;
  const canManageShared = !!session?.permissions.includes("settings.write");
  const [name, setName] = useState("");
  const [scope, setScope] = useState<ViewScope>("private");
  const createView = useCreateView();

  function submit() {
    if (!name.trim()) {
      toast.error("Give the view a name");
      return;
    }
    createView.mutate(
      {
        entity_type: entityType,
        name: name.trim(),
        filter: initialState.filter ?? null,
        columns: initialState.columns,
        sort: initialState.sort ?? null,
        scope,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setName("");
          setScope("private");
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Save view
          </DialogTitle>
          <DialogDescription>
            Capture the current filter + visible columns + sort as a named
            tab on this page. Private views are only visible to you;
            shared views appear for every internal user.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="view-name" className="text-xs">
              Name
            </Label>
            <Input
              id="view-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Hot leads"
              autoFocus
              maxLength={200}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="view-scope" className="text-xs">
              Scope
            </Label>
            <Select
              value={scope}
              onValueChange={(v) => setScope(v as ViewScope)}
            >
              <SelectTrigger id="view-scope" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Private</SelectItem>
                {canManageShared && (
                  <SelectItem value="shared">Shared</SelectItem>
                )}
              </SelectContent>
            </Select>
            {!canManageShared && (
              <p className="text-[11px] text-muted-foreground">
                Sharing requires the <code>settings.write</code> permission.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={createView.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={createView.isPending || !name.trim()}
          >
            {createView.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save view
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
